'use client'

import React, { useState, useEffect } from 'react'
import { Star, ChevronLeft, ChevronRight, Check } from 'lucide-react'
import { reviews } from '../data/Reviews'

const ReviewCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [isMobile, setIsMobile] = useState(false)

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768) // md breakpoint
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Auto-play: move forward every 4 seconds
  useEffect(() => {
    if (!isAutoPlaying) return

    const timer = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % reviews.length)
    }, 4000)

    return () => clearInterval(timer)
  }, [isAutoPlaying])

  const goToNext = () => {
    setCurrentIndex(prev => (prev + 1) % reviews.length)
    setIsAutoPlaying(false)
  }

  const goToPrev = () => {
    setCurrentIndex(prev => (prev - 1 + reviews.length) % reviews.length)
    setIsAutoPlaying(false)
  }

  // Get visible reviews (responsive: 1 on mobile, 3 on desktop)
  const getVisibleReviews = () => {
    const visible = []
    const numToShow = isMobile ? 1 : 3
    
    for (let i = 0; i < numToShow; i++) {
      const index = (currentIndex + i) % reviews.length
      visible.push(reviews[index])
    }
    return visible
  }

  const visibleReviews = getVisibleReviews()

  return (
    <div className="relative max-w-6xl mx-auto">
      {/* Navigation Arrows */}
      <button
        onClick={goToPrev}
        aria-label="Previous review"
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 z-10 bg-white rounded-full p-2 md:p-3 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:bg-gray-50 group"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-blue-600" />
      </button>

      <button
        onClick={goToNext}
        aria-label="Next review"
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 z-10 bg-white rounded-full p-2 md:p-3 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 hover:bg-gray-50 group"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-gray-600 group-hover:text-blue-600" />
      </button>

      {/* Reviews Grid - Responsive */}
      <div className={`grid gap-6 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}>
        {visibleReviews.map((review, index) => (
          <div 
            key={`${review.id}-${currentIndex}-${index}`}
            className="bg-gray-50 rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300"
          >
            {/* Stars */}
            <div className="flex items-center space-x-1 mb-4">
              {[1,2,3,4,5].map(star => (
                <Star 
                  key={star} 
                  className={`w-5 h-5 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            
            {/* Review Text */}
            <p className="text-gray-700 leading-relaxed mb-4">
              "{review.review}"
            </p>
            
            {/* Author */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex items-center space-x-2 mb-1">
                <div className="font-semibold text-gray-800">{review.name}</div>
                <div className="flex items-center space-x-1 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="w-3 h-3" />
                  <span>Verified</span>
                </div>
              </div>
              <div className="text-sm text-gray-600">{review.title}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Dots Indicator */}
      {isMobile && (
        <div className="flex justify-center mt-6 space-x-2">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setCurrentIndex(index)
                setIsAutoPlaying(false)
              }}
              aria-label={`Go to review ${index + 1}`}
              aria-current={index === currentIndex ? 'true' : 'false'}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default ReviewCarousel